import * as React from 'react';

import { useDebouncedFlush } from './useDeferredFlush';

type DebouncedDispatch<A> = (
    value: React.SetStateAction<A>,
    options?: { debounce: boolean }
) => void;

/**
 * A useState handling two states: a real time one and a debounced one.
 * A delay option can be specified to customize the delay. Default: 500ms.
 *
 * setStateDispatcher options:
 *  - debounce: boolean, default true. If set to false will set both states
 *              without debouncing. Useful for (re)setting state.
 *
 * Usage examples:
 *   const [value, debouncedValue, setValue] = useDebouncedState<string>('');
 *
 *   // `value` will be updated instantly, `debouncedValue` after a delay.
 *   setValue('new value');
 *
 *   // `value` and `debouncedValue` will be both updated instantly.
 *   setValue('new value', { debounce: false })
 *
 */
export function useDebouncedState<S>(
    defaultValue: S,
    options: { delay: number } = { delay: 500 }
): [S, S, DebouncedDispatch<S>] {
    const { delay } = options;
    const [state, setState] = React.useState<S>(defaultValue);
    const [debouncedState, setDebouncedState] = React.useState<S>(defaultValue);
    const debounce = useDebouncedFlush(delay);

    const setStateDispatcher = (
        newState: React.SetStateAction<S>,
        options = { debounce: true }
    ) => {
        setState(newState);

        if (options.debounce === false) {
            setDebouncedState(newState);
            return;
        }

        debounce(() => {
            setDebouncedState(newState);
        });
    };

    return [state, debouncedState, setStateDispatcher];
}
